(() => {
  'use strict';

  /* =========================================
     WAMBOLT TOPOGRAPHY
     ========================================= */

  if (window.WamboltTopographyLoaded) {
    return;
  }

  window.WamboltTopographyLoaded = true;


  /* =========================================
     SELECTORS
     ========================================= */

  const ACTIVE_SELECTOR = [
    '[data-wambolt-topography="full"]',
    '[data-wambolt-topography="fade"]',
  ].join(',');

  const BARBA_CONTAINER_SELECTOR =
    '[data-barba="container"]';

  const BARBA_WRAPPER_SELECTOR =
    '[data-barba="wrapper"]';

  const HORIZONTAL_SPACE_SELECTOR =
    '[data-wambolt-topography-space="horizontal-story"]';

  const HORIZONTAL_PANEL_SELECTOR =
    '[data-horizontal-scroll-panel]';


  /* =========================================
     HORIZONTAL STORY BREAKPOINT
     ========================================= */

  const horizontalStoryMedia =
    window.matchMedia(
      '(min-width: 768px)'
    );


  /* =========================================
     STATE
     ========================================= */

  let resizeObserver = null;
  let mutationObserver = null;

  let firstFrame = null;
  let secondFrame = null;

  let initialized = false;


  /* =========================================
     ACTIVE ELEMENTS
     ========================================= */

  function getActiveElements(
    root = document
  ) {
    const elements = [];


    if (
      root instanceof Element &&
      root.matches(ACTIVE_SELECTOR)
    ) {
      elements.push(root);
    }


    if (
      root &&
      typeof root.querySelectorAll ===
        'function'
    ) {
      elements.push(
        ...root.querySelectorAll(
          ACTIVE_SELECTOR
        )
      );
    }


    return [
      ...new Set(elements),
    ];
  }


  /* =========================================
     BARBA COORDINATE ORIGIN
     ========================================= */

  function getCurrentBarbaContainer() {
    return document.querySelector(
      BARBA_CONTAINER_SELECTOR
    );
  }


  function getCoordinateOrigin(
    element
  ) {
    /*
     * Normal page sections use the current
     * Barba container as their common pattern
     * coordinate system.
     *
     * Persistent elements such as the footer
     * are outside that container, so they fall
     * back to the current page container.
     */

    return (
      element.closest(
        BARBA_CONTAINER_SELECTOR
      ) ||
      getCurrentBarbaContainer() ||
      document.documentElement
    );
  }


  /* =========================================
     HORIZONTAL STORY
     ========================================= */

  function getHorizontalStory(
    element
  ) {
    if (
      !horizontalStoryMedia.matches
    ) {
      return null;
    }


    const story =
      element.closest(
        HORIZONTAL_SPACE_SELECTOR
      );


    if (!story) {
      return null;
    }


    /*
     * Width alone is not enough.
     *
     * At 768px+ reduced-motion mode disables
     * the horizontal pin. In that case the
     * panels need normal page coordinates.
     */

    if (
      story.getAttribute(
        'data-horizontal-story-mode'
      ) !==
      'horizontal'
    ) {
      return null;
    }


    if (
      !element.matches(
        HORIZONTAL_PANEL_SELECTOR
      )
    ) {
      return null;
    }


    return story;
  }


  function getHorizontalPanels(
    story
  ) {
    return Array.from(
      story.children
    ).filter(
      (child) =>
        child instanceof Element &&
        child.matches(
          HORIZONTAL_PANEL_SELECTOR
        )
    );
  }


  function getPanelTranslatedX(
    story,
    panel
  ) {
    const storyRect =
      story.getBoundingClientRect();

    const panelRect =
      panel.getBoundingClientRect();


    /*
     * panel.offsetLeft is its untransformed
     * location within the horizontal story.
     *
     * Comparing that layout position with its
     * rendered bounding rectangle tells us how
     * far GSAP has translated the panel.
     *
     * The story/root's own pinned positioning
     * is subtracted out by using storyRect.left.
     */

    const expectedLeft =
      storyRect.left +
      panel.offsetLeft;


    return (
      panelRect.left -
      expectedLeft
    );
  }


  function alignHorizontalStoryElement(
    element
  ) {
    const story =
      getHorizontalStory(element);


    if (!story) {
      return false;
    }


    const panels =
      getHorizontalPanels(story);


    if (!panels.length) {
      return false;
    }


    const firstPanel =
      panels[0];


    if (
      !panels.includes(element)
    ) {
      return false;
    }


    const translatedX =
      getPanelTranslatedX(
        story,
        element
      );


    /*
     * Establish one stationary texture canvas
     * for the entire pinned Horizontal Story.
     *
     * Base phase:
     *
     *   first panel = 0
     *   second panel = -100vw
     *   third panel = -200vw
     *
     * Then subtract the panel's current GSAP
     * transform.
     *
     * This makes the contour artwork visually
     * stationary in the viewport while the
     * panel itself moves horizontally.
     */

    const offsetX =
      firstPanel.offsetLeft -
      element.offsetLeft -
      translatedX;


    /*
     * The vertical phase remains completely
     * fixed during the pinned story.
     */

    const offsetY =
      0;


    element.style.setProperty(
      '--wambolt-topography-x',
      `${offsetX}px`
    );


    element.style.setProperty(
      '--wambolt-topography-y',
      `${offsetY}px`
    );


    return true;
  }


  /* =========================================
     STANDARD PAGE ALIGNMENT
     ========================================= */

  function alignStandardElement(
    element
  ) {
    const origin =
      getCoordinateOrigin(element);


    if (!origin) {
      return;
    }


    const originRect =
      origin.getBoundingClientRect();


    const elementRect =
      element.getBoundingClientRect();


    /*
     * These offsets make every normal
     * topographic region behave as though
     * the image were painted once across
     * the shared page coordinate space.
     */

    const offsetX =
      originRect.left -
      elementRect.left;


    const offsetY =
      originRect.top -
      elementRect.top;


    element.style.setProperty(
      '--wambolt-topography-x',
      `${offsetX}px`
    );


    element.style.setProperty(
      '--wambolt-topography-y',
      `${offsetY}px`
    );
  }


  /* =========================================
     ELEMENT ALIGNMENT
     ========================================= */

  function alignElement(
    element
  ) {
    if (
      !(element instanceof HTMLElement)
    ) {
      return;
    }


    /*
     * Horizontal Story gets a dedicated,
     * stationary texture coordinate system
     * only while its real pinned mode is active.
     */

    if (
      alignHorizontalStoryElement(
        element
      )
    ) {
      return;
    }


    /*
     * Everything else keeps using the normal
     * page-level seamless alignment system.
     */

    alignStandardElement(
      element
    );
  }


  /* =========================================
     RESIZE OBSERVER
     ========================================= */

  function refreshResizeObserver(
    elements
  ) {
    if (
      !('ResizeObserver' in window)
    ) {
      return;
    }


    if (!resizeObserver) {
      resizeObserver =
        new ResizeObserver(() => {
          scheduleAlignment();
        });
    }


    resizeObserver.disconnect();


    const targets =
      new Set();


    const container =
      getCurrentBarbaContainer();


    if (container) {
      targets.add(container);
    }


    elements.forEach(
      (element) => {
        targets.add(element);


        const horizontalSpace =
          element.closest(
            HORIZONTAL_SPACE_SELECTOR
          );


        if (horizontalSpace) {
          targets.add(
            horizontalSpace
          );
        }
      }
    );


    targets.forEach(
      (target) => {
        if (
          target instanceof Element
        ) {
          resizeObserver.observe(
            target
          );
        }
      }
    );
  }


  /* =========================================
     ALIGN ALL
     ========================================= */

  function alignAll() {
    const elements =
      getActiveElements(document);


    elements.forEach(
      (element) => {
        alignElement(element);
      }
    );


    refreshResizeObserver(
      elements
    );
  }


  /* =========================================
     SCHEDULING
     ========================================= */

  function cancelScheduledAlignment() {
    if (firstFrame !== null) {
      cancelAnimationFrame(
        firstFrame
      );

      firstFrame = null;
    }


    if (secondFrame !== null) {
      cancelAnimationFrame(
        secondFrame
      );

      secondFrame = null;
    }
  }


  function scheduleAlignment() {
    cancelScheduledAlignment();


    /*
     * Two frames gives Shopify, Barba,
     * responsive layout and component CSS
     * time to settle before coordinates are
     * measured.
     */

    firstFrame =
      requestAnimationFrame(() => {
        firstFrame = null;


        secondFrame =
          requestAnimationFrame(() => {
            secondFrame = null;

            alignAll();
          });
      });
  }


  /* =========================================
     PUBLIC API
     ========================================= */

  window.WamboltTopography = {
    refresh:
      scheduleAlignment
  };


  /* =========================================
     MUTATION HELPERS
     ========================================= */

  function nodeContainsRelevantContent(
    node
  ) {
    if (
      !(node instanceof Element)
    ) {
      return false;
    }


    if (
      node.matches(
        ACTIVE_SELECTOR
      ) ||
      node.matches(
        BARBA_CONTAINER_SELECTOR
      ) ||
      node.matches(
        HORIZONTAL_SPACE_SELECTOR
      )
    ) {
      return true;
    }


    return Boolean(
      node.querySelector(
        [
          ACTIVE_SELECTOR,
          BARBA_CONTAINER_SELECTOR,
          HORIZONTAL_SPACE_SELECTOR,
        ].join(',')
      )
    );
  }


  function mutationIsRelevant(
    mutation
  ) {
    if (
      mutation.type !==
      'childList'
    ) {
      return false;
    }


    const addedNodes =
      Array.from(
        mutation.addedNodes
      );


    const removedNodes =
      Array.from(
        mutation.removedNodes
      );


    return [
      ...addedNodes,
      ...removedNodes,
    ].some(
      (node) =>
        nodeContainsRelevantContent(
          node
        )
    );
  }


  /* =========================================
     MUTATION OBSERVER
     ========================================= */

  function startMutationObserver() {
    if (
      !('MutationObserver' in window)
    ) {
      return;
    }


    if (mutationObserver) {
      mutationObserver.disconnect();
    }


    const target =
      document.querySelector(
        BARBA_WRAPPER_SELECTOR
      ) ||
      document.body;


    if (!target) {
      return;
    }


    mutationObserver =
      new MutationObserver(
        (mutations) => {
          if (
            mutations.some(
              mutationIsRelevant
            )
          ) {
            scheduleAlignment();
          }
        }
      );


    mutationObserver.observe(
      target,
      {
        childList:
          true,

        subtree:
          true,
      }
    );
  }


  /* =========================================
     MEDIA LOADS
     ========================================= */

  function handleMediaLoad(
    event
  ) {
    const target =
      event.target;


    if (
      target instanceof HTMLImageElement ||
      target instanceof HTMLVideoElement
    ) {
      scheduleAlignment();
    }
  }


  /* =========================================
     SHOPIFY EVENTS
     ========================================= */

  function handleShopifyChange() {
    scheduleAlignment();
  }


  /* =========================================
     RESPONSIVE CHANGE
     ========================================= */

  function handleHorizontalBreakpoint() {
    /*
     * Crossing 768px changes Horizontal Story
     * between normal page coordinates and its
     * dedicated horizontal coordinate space.
     */

    scheduleAlignment();
  }


  /* =========================================
     INITIALIZATION
     ========================================= */

  function init() {
    if (initialized) {
      scheduleAlignment();

      return;
    }


    initialized =
      true;


    startMutationObserver();


    document.addEventListener(
      'load',
      handleMediaLoad,
      true
    );


    window.addEventListener(
      'resize',
      scheduleAlignment,
      {
        passive:
          true,
      }
    );


    window.addEventListener(
      'orientationchange',
      scheduleAlignment,
      {
        passive:
          true,
      }
    );


    window.addEventListener(
      'pageshow',
      scheduleAlignment
    );


    /*
     * Wambolt runtime events.
     *
     * Some Wambolt runtime events are emitted
     * on window, so listen there explicitly.
     */

    window.addEventListener(
      'wambolt:lenis:ready',
      scheduleAlignment
    );


    window.addEventListener(
      'wambolt:page:enter',
      scheduleAlignment
    );


    /*
     * Retain document listeners as well for
     * compatibility with any existing local
     * dispatchers.
     */

    document.addEventListener(
      'wambolt:lenis:ready',
      scheduleAlignment
    );


    document.addEventListener(
      'wambolt:page:enter',
      scheduleAlignment
    );


    /*
     * Explicit refresh request used by motion
     * components such as Horizontal Story.
     */

    window.addEventListener(
      'wambolt:topography:refresh',
      scheduleAlignment
    );


    document.addEventListener(
      'wambolt:topography:refresh',
      scheduleAlignment
    );


    /*
     * Shopify Theme Editor.
     */

    document.addEventListener(
      'shopify:section:load',
      handleShopifyChange
    );


    document.addEventListener(
      'shopify:section:unload',
      handleShopifyChange
    );


    document.addEventListener(
      'shopify:section:reorder',
      handleShopifyChange
    );


    /*
     * Horizontal Story's special breakpoint.
     */

    if (
      typeof horizontalStoryMedia
        .addEventListener ===
      'function'
    ) {
      horizontalStoryMedia
        .addEventListener(
          'change',
          handleHorizontalBreakpoint
        );
    } else if (
      typeof horizontalStoryMedia
        .addListener ===
      'function'
    ) {
      horizontalStoryMedia
        .addListener(
          handleHorizontalBreakpoint
        );
    }


    /*
     * Font loading can change dimensions and
     * therefore section geometry.
     */

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready
        .then(() => {
          scheduleAlignment();
        })
        .catch(() => {});
    }


    scheduleAlignment();
  }


  /* =========================================
     BOOT
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once:
          true,
      }
    );
  } else {
    init();
  }
})();